import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Shuffle, ArrowRight } from "lucide-react";
import BottleSilhouette from "../components/BottleSilhouette";
import DnaRadar from "../components/DnaRadar";
import { getSurpriseOptions, surprise } from "../lib/api";
import { getCategoryColor } from "../lib/constants";
import { recordEvent } from "../lib/adminApi";

export default function Surprise() {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [reel, setReel] = useState([]);

  useEffect(() => { getSurpriseOptions().then((d) => setOptions(d.options)); }, []);

  const spin = async (option) => {
    setSelected(option);
    setSpinning(true);
    setResult(null);
    recordEvent("surprise_spin", { extra: { option } });

    // Pre-fetch result for the reveal
    const res = await surprise(option);
    // Build a reel of 8 fake names + the real one at the end
    const teaser = ["Choosing", "Sniffing", "Pouring", "Considering", "Reading DNA", "Almost there", "Holding breath", "Revealing"];
    setReel(teaser);

    setTimeout(() => {
      setSpinning(false);
      setResult(res);
    }, 2400);
  };

  const reset = () => { setSelected(null); setResult(null); setSpinning(false); };

  return (
    <div className="px-5 md:px-12 max-w-6xl mx-auto py-10 min-h-[80vh]" data-testid="surprise-page">
      <p className="overline text-[#C17F59] mb-3">Surprise Me</p>
      <h1 className="font-display text-5xl sm:text-7xl tracking-tight leading-[0.95] mb-3">
        Don't choose. <em className="not-italic text-[#C17F59]">Let the Atlas choose for you.</em>
      </h1>
      <p className="text-white/65 max-w-xl mb-10">Pick a mood. Watch the roulette spin. Trust the bartender.</p>

      {/* Option grid */}
      <AnimatePresence mode="wait">
        {!selected && (
          <motion.div
            key="grid"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
            data-testid="surprise-options"
          >
            {options.map((opt, i) => (
              <motion.button
                key={opt}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ y: -3 }}
                onClick={() => spin(opt)}
                className="group bg-[#15171A] border border-white/8 hover:border-[#C17F59] p-7 text-left transition-colors duration-300 relative overflow-hidden"
                data-testid={`surprise-option-${opt.replace(/\s/g, '-')}`}
              >
                <div className="absolute -top-10 -right-10 w-40 h-40 opacity-0 group-hover:opacity-30 transition-opacity duration-700"
                     style={{ background: "radial-gradient(circle, rgba(193,127,89,0.5), transparent 60%)" }} />
                <Shuffle size={16} className="text-[#C17F59] mb-3" />
                <p className="font-display text-3xl">{opt}</p>
                <ArrowRight size={14} className="absolute bottom-5 right-5 text-white/30 group-hover:text-[#C17F59] group-hover:translate-x-1 transition-all" />
              </motion.button>
            ))}
          </motion.div>
        )}

        {/* SPIN STAGE */}
        {selected && spinning && (
          <motion.div
            key="spinning"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-16"
            data-testid="surprise-spinning"
          >
            <p className="overline text-[#C17F59] mb-6">{selected}</p>
            <div className="relative h-24 w-full max-w-md overflow-hidden border-y border-[#C17F59]/40">
              <div className="absolute inset-0 bg-gradient-to-r from-[#0E0F0C] via-transparent to-[#0E0F0C] z-10 pointer-events-none" />
              <div className="roulette-strip absolute left-0 right-0 flex flex-col items-center gap-3 text-center">
                {reel.map((t, i) => (
                  <p key={i} className="font-display text-3xl h-24 leading-[6rem]">{t}</p>
                ))}
              </div>
            </div>
            <p className="text-xs text-white/50 mt-6 uppercase tracking-[0.3em]">Reading the atlas...</p>
          </motion.div>
        )}

        {/* RESULT */}
        {result && !spinning && (
          <motion.div
            key="result"
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center py-6"
            data-testid="surprise-result"
          >
            <div className="relative bg-[#15171A] border border-white/8 p-10 flex items-center justify-center min-h-[420px] grain">
              <div className="absolute inset-0"
                   style={{ background: `radial-gradient(circle at 50% 60%, ${getCategoryColor(result.pick.category)}33, transparent 75%)` }} />
              <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                          transition={{ type: "spring", stiffness: 140, damping: 16 }}>
                <BottleSilhouette category={result.pick.category} size={160} />
              </motion.div>
            </div>
            <div className="space-y-5">
              <p className="overline text-[#C17F59]">{selected}</p>
              <h2 className="font-display text-5xl sm:text-6xl tracking-tight leading-[0.95]">
                The Atlas says: <em className="not-italic text-[#C17F59]">{result.pick.name}</em>.
              </h2>
              <p className="text-base md:text-lg text-white/75 italic font-display">"{result.pick.hook}"</p>
              <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm pt-2">
                <div><span className="overline text-white/40">Country</span><div className="font-display text-xl">{result.pick.country}</div></div>
                <div><span className="overline text-white/40">Price · 30ml</span><div className="font-display text-xl">₹{result.pick.price}</div></div>
                <div><span className="overline text-white/40">ABV</span><div className="font-display text-xl">{result.pick.abv}%</div></div>
              </div>
              <div className="border border-white/8 p-4">
                <DnaRadar items={[{ name: result.pick.name, dna: result.pick.dna, category: result.pick.category }]} height={220} />
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to={`/gin/${result.pick.id}`}
                      className="inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-6 py-3 text-xs uppercase tracking-[0.3em] hover:bg-[#D89A74] transition-colors"
                      data-testid="surprise-view-detail">
                  See full profile
                  <ArrowRight size={14} />
                </Link>
                <button onClick={reset}
                        className="inline-flex items-center gap-2 border border-white/15 px-6 py-3 text-xs uppercase tracking-[0.3em] hover:border-[#C17F59] hover:text-[#C17F59] transition-colors"
                        data-testid="surprise-again">
                  Spin again
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
